import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/cn";
import { getMonthOptions } from "@/lib/months";

interface MonthPickerProps {
  selected: string;
  onChange: (key: string) => void;
  count?: number;
}

export function MonthPicker({ selected, onChange, count = 12 }: MonthPickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const options = getMonthOptions(count);
  const current = options.find((o) => o.key === selected) ?? options[0];

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="pill bg-accent-blue/10 text-accent-blue border border-accent-blue/20 flex items-center gap-1.5 hover:bg-accent-blue/15 cursor-pointer"
      >
        {current.label}
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-30 w-48 rounded-md border border-border bg-white shadow-lg py-1 max-h-44 overflow-y-auto">
          {options.map((opt) => {
            const isSelected = opt.key === selected;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
                className={cn(
                  "w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-2",
                  isSelected
                    ? "bg-accent-blue/5 text-accent-blue font-medium"
                    : "text-slate-700 hover:bg-slate-50"
                )}
              >
                <span className="flex items-center gap-2">
                  {opt.label}
                  {opt.isCurrent && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                      Current
                    </span>
                  )}
                </span>
                {isSelected && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
